import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { History, Trash2, Gauge, TrendingUp, FileWarning, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { getSessions, deleteSession, API } from "../lib/api";
import { printSession } from "../lib/print";
import { useTranslation } from "react-i18next";

const BACKEND = API.replace(/\/api$/, "");

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

const PassPill = ({ rate }) => {
  const color = rate == null ? "#64748B" : rate === 100 ? "#10B981" : rate >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <span className="font-mono text-xs font-bold tracking-widest px-2.5 py-1 rounded-sm tabular-nums"
      style={{ color, background: `${color}26` }}>
      {rate == null ? "—" : `${rate}%`}
    </span>
  );
};

export default function SessionLog() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { data: sessions = [], isLoading } = useQuery({ queryKey: ["sessions"], queryFn: getSessions });

  const delMutation = useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      toast.success("Session deleted");
    },
  });

  const chartData = useMemo(() => {
    return [...sessions].reverse().map((s) => ({
      name: fmtDate(s.createdAt),
      "FL Camber": s.measurements?.frontLeft?.camber ?? null,
      "FR Camber": s.measurements?.frontRight?.camber ?? null,
      "Front Toe": s.measurements?.frontToe ?? null,
    }));
  }, [sessions]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 animate-rise">
        <div className="flex items-center gap-2 font-mono text-xs text-amber tracking-widest uppercase">
          <History size={14} /> {t("log.kicker")}
        </div>
        <h1 className="font-heading font-black uppercase tracking-wider text-3xl sm:text-4xl lg:text-5xl mt-2">{t("log.title")}</h1>
        <p className="text-slate-400 text-sm mt-2">Your saved alignment sessions, newest first.</p>
      </div>

      {isLoading ? (
        <p className="font-mono text-sm text-slate-500" data-testid="log-loading">Loading sessions…</p>
      ) : sessions.length === 0 ? (
        <div data-testid="log-empty" className="rounded-md bg-surface border border-steel p-12 text-center animate-rise">
          <FileWarning className="mx-auto text-slate-600 mb-4" size={40} />
          <h2 className="font-heading font-bold uppercase tracking-widest text-lg text-slate-300">No sessions yet</h2>
          <p className="text-slate-500 text-sm mt-2 mb-6">Run an alignment check and save it to start building history.</p>
          <Link to="/">
            <Button data-testid="btn-go-calculator" className="bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold">
              <Gauge size={15} /> Open Calculator
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {sessions.length >= 2 && (
            <section data-testid="drift-chart" className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-amber" />
                <h2 className="font-heading font-bold uppercase tracking-widest text-lg">Alignment Drift</h2>
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A364F" />
                    <XAxis dataKey="name" stroke="#64748B" fontSize={11} fontFamily="'Share Tech Mono'" />
                    <YAxis stroke="#64748B" fontSize={11} fontFamily="'Share Tech Mono'" />
                    <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #2A364F", borderRadius: 6, fontFamily: "'Share Tech Mono'", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontFamily: "'Share Tech Mono'", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }} />
                    <Line type="monotone" dataKey="FL Camber" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="FR Camber" stroke="#0284C7" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Front Toe" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sessions.map((s) => (
              <article key={s.id} data-testid={`session-card-${s.id}`}
                className="rounded-md bg-surface border border-steel p-5 animate-rise hover:border-amber/50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-semibold text-base text-white">{s.title}</h3>
                    <p className="font-mono text-[11px] text-slate-500 tracking-wide mt-1">
                      {fmtDate(s.createdAt)}{s.mileage ? ` · ${s.mileage.toLocaleString()} mi` : ""}{s.treadDepth ? ` · ${s.treadDepth}` : ""}
                    </p>
                  </div>
                  <PassPill rate={s.passRate} />
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 font-mono text-xs">
                  <div className="rounded-sm bg-ink border border-steel/60 p-2 text-center">
                    <div className="text-slate-500 text-[9px] tracking-widest">FL CAM</div>
                    <div className="text-white tabular-nums mt-0.5">{s.measurements?.frontLeft?.camber ?? "—"}</div>
                  </div>
                  <div className="rounded-sm bg-ink border border-steel/60 p-2 text-center">
                    <div className="text-slate-500 text-[9px] tracking-widest">FR CAM</div>
                    <div className="text-white tabular-nums mt-0.5">{s.measurements?.frontRight?.camber ?? "—"}</div>
                  </div>
                  <div className="rounded-sm bg-ink border border-steel/60 p-2 text-center">
                    <div className="text-slate-500 text-[9px] tracking-widest">F TOE</div>
                    <div className="text-white tabular-nums mt-0.5">{s.measurements?.frontToe ?? "—"}</div>
                  </div>
                </div>

                {s.notes && <p className="text-xs text-slate-400 mt-3 leading-relaxed line-clamp-2">{s.notes}</p>}

                {s.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid={`session-photos-${s.id}`}>
                    {s.photos.map((ph, i) => (
                      <div key={i} className="relative">
                        <img src={`${BACKEND}${ph.url}`} alt={ph.corner}
                          className="w-14 h-14 object-cover rounded-sm border border-steel" />
                        <span className="absolute bottom-0 left-0 right-0 bg-ink/80 font-mono text-[8px] text-amber text-center tracking-widest">{ph.corner}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-1 mt-4">
                  <Button data-testid={`btn-print-${s.id}`} variant="ghost" size="sm"
                    onClick={() => { if (!printSession(s)) toast.error("Allow pop-ups to print the report."); }}
                    className="text-slate-500 hover:text-cyan hover:bg-cyan/10 font-mono text-[10px] uppercase tracking-widest">
                    <Printer size={13} /> {t("log.print")}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`btn-delete-${s.id}`} variant="ghost" size="sm"
                        className="text-slate-500 hover:text-fail hover:bg-fail/10 font-mono text-[10px] uppercase tracking-widest">
                        <Trash2 size={13} /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-surface border-steel text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading uppercase tracking-widest">Delete session?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          This permanently removes "{s.title}" from your log.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`btn-confirm-delete-${s.id}`} onClick={() => delMutation.mutate(s.id)}
                          className="bg-fail text-white hover:bg-fail/80">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
